"use client";

import * as React from "react";
import { cn } from "../lib/cn";
import { useReducedMotion } from "../hooks/useReducedMotion";

/**
 * MoodLighting — the ambient gradient *field* behind a reserved moment.
 *
 * A full-bleed, decorative backdrop for the small set of surfaces that earn
 * depth (hero greeting, empty states, onboarding, the dark preview stage). It
 * composes two layers:
 *
 *   Layer A — a slow, transform-only drift of two soft radial blobs anchored
 *             just inside opposite corners (`--mood-field-1` / `--mood-field-2`)
 *             so their mass is on-canvas and the calm 14s drift is perceptible
 *             at rest. Painted at full low alpha from the first frame so
 *             html-to-image / PDF export never captures a blank backdrop. When
 *             `hoverResponse` is set, the field brightens + lifts slightly while
 *             the pointer is over the host (a clear, opt-in hover wake).
 *   Layer B — an optional cursor glow (`--mood-glow`) that tracks the pointer
 *             within the host when `interactive`, fading in over 240ms.
 *
 * This is the *field* layer and is deliberately distinct from the lib's
 * perimeter/border effects — do not reach for it when you mean one of those:
 *
 *   - `<BorderGlow>`  — steady/pulsing halo around a surface's perimeter.
 *   - `<EditingBeam>` — a beam gliding across a block being rewritten.
 *   - `<BorderBeam>`  — a bright arc sweeping the border (Badge "premium").
 *
 * MoodLighting wraps no content: it renders an `absolute inset-0
 * pointer-events-none overflow-hidden z-0` `aria-hidden` backdrop, meant to be
 * dropped as the FIRST child of a `position:relative` host. Foreground content
 * sits as a sibling at a higher z-index on its own legibility surface.
 *
 *     <section className="relative">
 *       <MoodLighting tone="light" interactive />
 *       <div className="relative z-10">…hero content…</div>
 *     </section>
 *
 * `tone="stage"` paints a FIXED dark ink mat (`--mood-stage-bg`) with a
 * high-alpha corner glow (`--mood-stage-glow`) for dark hero/preview surfaces.
 * It is a local dark backdrop, NOT a theme switch — it does not toggle the
 * app's dark mode, and the mat stays dark in both light and dark themes.
 *
 * Every color resolves through the `--mood-*` token contract (which defaults
 * to `--accent` / `--primary` by name) — there is no raw hex in the component
 * or its rendered styles.
 *
 * SSR-safe (no `window` at module/render top level), and reduced-motion aware:
 * the drift freezes (via the preset's global reduced-motion kill-list) and the
 * glow fade collapses to 0ms (the pointer-follow itself is direct
 * manipulation, not autonomous motion, so it is allowed to continue).
 */

interface IntensityStep {
  /** Alpha multiplier for the drifting field blobs. */
  field: number;
  /** Alpha multiplier for the cursor / stage glow. */
  glow: number;
}

const INTENSITY: Record<NonNullable<MoodLightingProps["intensity"]>, IntensityStep> = {
  subtle: { field: 1, glow: 0.7 },
  default: { field: 1.4, glow: 1 },
};

export interface MoodLightingProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Which surface the field is painted for. `light` (default) follows the
   * app's light/dark tokens; `stage` paints a fixed dark ink mat for dark
   * hero/preview surfaces and does NOT toggle the app's dark mode.
   */
  tone?: "light" | "stage";
  /** Enable the cursor glow that tracks the pointer within the host. Default `false`. */
  interactive?: boolean;
  /**
   * Brighten + lift the ambient field while the pointer is over the host — an
   * opt-in hover wake on top of the always-on drift. Default `false` to
   * preserve the backdrop-is-decoration contract (the card-lift itself is a
   * consumer concern; see the JSDoc usage note). Independent of `interactive`,
   * though they compose: with both on, hovering also wakes the cursor glow.
   */
  hoverResponse?: boolean;
  /** Field + glow strength. Default `subtle`. */
  intensity?: "subtle" | "default";
  /** Cursor-glow radius in px. Default `560`. */
  glowRadius?: number;
}

export const MoodLighting = React.forwardRef<HTMLDivElement, MoodLightingProps>(
  (
    {
      className,
      tone = "light",
      interactive = false,
      hoverResponse = false,
      intensity = "subtle",
      glowRadius = 560,
      style,
      ...props
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    // `null` (pre-resolution / SSR) is treated as "allow motion" so the first
    // paint is not janky. Only an explicit `true` freezes the drift + makes the
    // glow fade instant.
    const reducedMotion = reduced === true;

    const step = INTENSITY[intensity];
    const isStage = tone === "stage";

    // The local backdrop element. Used both to forward the caller's ref and as
    // the origin rect the pointer position is measured against, so the glow
    // tracks within the surface (not the viewport). The pointer listeners
    // themselves attach to its parent (the relative consumer host), because the
    // backdrop is `pointer-events-none` and so never receives pointer events.
    const hostRef = React.useRef<HTMLDivElement | null>(null);
    const glowRef = React.useRef<HTMLDivElement | null>(null);
    const fieldRef = React.useRef<HTMLDivElement | null>(null);
    const setRefs = React.useCallback(
      (node: HTMLDivElement | null) => {
        hostRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [ref],
    );

    // Pointer plumbing shared by the cursor glow (`interactive`) and the hover
    // wake (`hoverResponse`). Both want pointerenter/leave on the consumer host,
    // so they fold into one effect, gated on either flag. The listeners attach
    // to the backdrop's PARENT (the relative host) because the backdrop is
    // pointer-events-none; positions are still measured against the backdrop
    // rect. The pointermove rAF coalescer only matters for the cursor glow.
    //
    //   - `interactive`: one write per frame to the glow's `--mx`/`--my`
    //     (scoped to the host rect so the glow tracks within the surface even
    //     when it scrolls/resizes), and `--glow-opacity` 0->1 on enter so the
    //     glow wakes on entry rather than only on first move.
    //   - `hoverResponse`: flip the field layer's `--hover` 0->1 on enter so it
    //     brightens + lifts (the CSS reads `--hover`; reduced motion keeps the
    //     state change but drops the transition — see fieldStyle).
    //
    // SSR-safe — only runs inside an effect.
    React.useEffect(() => {
      if (!interactive && !hoverResponse) return;
      const backdrop = hostRef.current;
      if (!backdrop) return;
      // The backdrop itself is `pointer-events-none` (it must never intercept
      // clicks meant for the foreground content), so it never receives pointer
      // events and could never drive the glow. Listen on its parent instead —
      // the relative consumer host the backdrop is dropped into. The pointer
      // position is still measured against the BACKDROP rect (inset-0 over the
      // host, so they share an origin) so the glow tracks within the surface.
      const host = backdrop.parentElement;
      if (!host) return;

      let frame: number | null = null;
      let latestX = 0;
      let latestY = 0;

      const apply = () => {
        frame = null;
        const glow = glowRef.current;
        if (!glow) return;
        glow.style.setProperty("--mx", `${latestX}px`);
        glow.style.setProperty("--my", `${latestY}px`);
      };

      const onMove = (event: PointerEvent) => {
        const rect = backdrop.getBoundingClientRect();
        latestX = event.clientX - rect.left;
        latestY = event.clientY - rect.top;
        if (frame !== null) return;
        frame = requestAnimationFrame(apply);
      };
      const onEnter = () => {
        if (interactive) glowRef.current?.style.setProperty("--glow-opacity", "1");
        if (hoverResponse) fieldRef.current?.style.setProperty("--hover", "1");
      };
      const onLeave = () => {
        if (interactive) glowRef.current?.style.setProperty("--glow-opacity", "0");
        if (hoverResponse) fieldRef.current?.style.setProperty("--hover", "0");
      };

      if (interactive) host.addEventListener("pointermove", onMove, { passive: true });
      host.addEventListener("pointerenter", onEnter);
      host.addEventListener("pointerleave", onLeave);
      return () => {
        if (interactive) host.removeEventListener("pointermove", onMove);
        host.removeEventListener("pointerenter", onEnter);
        host.removeEventListener("pointerleave", onLeave);
        if (frame !== null) cancelAnimationFrame(frame);
      };
    }, [interactive, hoverResponse]);

    // ── Layer A: ambient drifting field ──────────────────────────────────
    // Two soft radial blobs anchored to opposite corners. On `stage` the
    // field reads against the fixed dark mat with the higher-alpha stage glow;
    // on `light` it reads against the app surface with the field tokens.
    const fieldColorTop = isStage ? "var(--mood-stage-glow)" : "var(--mood-field-1)";
    const fieldColorBottom = isStage ? "var(--mood-glow)" : "var(--mood-field-2)";
    const fieldStyle: React.CSSProperties = {
      // Anchors sit just INSIDE opposite corners (was 12%/-10% and 104%/112%,
      // mostly off-box) so the blob mass is on-canvas and the drift is visible;
      // transparent stops pushed outward (was 46%/50%) so each blob has more
      // visible body to move. #160.
      backgroundImage: [
        `radial-gradient(circle at 18% 0%, ${fieldColorTop}, transparent 52%)`,
        `radial-gradient(circle at 96% 104%, ${fieldColorBottom}, transparent 56%)`,
      ].join(", "),
      // Base alpha scaled by intensity, brightened toward 1.4x while hovered
      // (`--hover` is flipped 0<->1 by the pointer effect when `hoverResponse`).
      // The token color is never touched — only the painted alpha. Drives the
      // hover wake via opacity (NOT transform: the drift animation owns
      // transform, so the card-lift is left to the consumer's own host).
      //
      // The opacity is routed through a custom property so the calc() survives
      // React's style serialization (React coerces a `calc()` string assigned
      // straight to `opacity` to NaN — see #160; custom props pass through
      // verbatim).
      ["--mood-field-opacity" as string]: `calc(${step.field} * (1 + 0.4 * var(--hover, 0)))`,
      opacity: "var(--mood-field-opacity)",
      // The brighten eases with the smooth-out token (240/320 family) when
      // motion is allowed; reduced motion collapses it to an instant state
      // change (this is a hover state, not autonomous motion — same rule the
      // glow fade follows below).
      transition: reducedMotion ? "none" : "opacity 320ms cubic-bezier(0.16, 1, 0.3, 1)",
    };

    // ── Layer B: cursor glow ─────────────────────────────────────────────
    // A single radial that tracks the pointer. Color comes from `--mood-glow`
    // (or `--mood-stage-glow` on a stage). Position is driven by the rAF
    // coalescer above via `--mx`/`--my`; opacity toggles via `--glow-opacity`.
    const glowColor = isStage ? "var(--mood-stage-glow)" : "var(--mood-glow)";
    const glowStyle: React.CSSProperties = {
      backgroundImage: `radial-gradient(${glowRadius}px circle at var(--mx, 50%) var(--my, 50%), ${glowColor}, transparent 62%)`,
      opacity: `calc(var(--glow-opacity, 0) * ${step.glow})`,
      // Glow fades in over 240ms with the out-soft entrance ease; reduced
      // motion collapses the fade to instant (pointer-follow still allowed).
      transition: reducedMotion ? "none" : "opacity 240ms cubic-bezier(0.22, 1, 0.36, 1)",
    };

    return (
      <div
        ref={setRefs}
        aria-hidden="true"
        className={cn(
          "absolute inset-0 z-0 overflow-hidden pointer-events-none",
          // On a stage, paint the fixed dark ink mat behind both layers.
          isStage && "bg-[hsl(var(--mood-stage-bg))]",
          className,
        )}
        style={style}
        {...props}
      >
        {/* Layer A — drifting ambient field. `animate-mood-drift` is in the
            preset's reduced-motion kill-list, so it freezes automatically
            under prefers-reduced-motion (no per-instance gate needed). */}
        <div
          ref={fieldRef}
          className={cn("absolute inset-0", !reducedMotion && "animate-mood-drift")}
          style={fieldStyle}
        />
        {/* Layer B — cursor glow (only mounted when interactive). */}
        {interactive ? <div ref={glowRef} className="absolute inset-0" style={glowStyle} /> : null}
      </div>
    );
  },
);
MoodLighting.displayName = "MoodLighting";
